import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  PlayArrow as PlayArrowIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { TestService } from '../services/testService'
import { Test, TestResult } from '../types/test'
import { QuestionCard } from '../components/test/QuestionCard'
import { TestTimer } from '../components/test/TestTimer'
import { FloatingTimer } from '../components/test/FloatingTimer'
import { useTestProgress } from '../hooks/useTestProgress'
import { TestResultModal } from '../components/test/TestResultModal'

export const TestPage: React.FC = () => {
  const { t } = useTranslation('test')
  const { testId } = useParams<{ testId: string }>()
  const [searchParams] = useSearchParams()
  const enrollmentId = searchParams.get('enrollmentId')
  const navigate = useNavigate()
  const { user } = useAuth()

  const [test, setTest] = useState<Test | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // テスト進行状態
  const [testStarted, setTestStarted] = useState(false)
  const [testStartTime, setTestStartTime] = useState<Date | null>(null)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<{ [questionId: string]: string }>({})

  // 提出関連
  const [submitting, setSubmitting] = useState(false)
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false)
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const [resultModalOpen, setResultModalOpen] = useState(false)

  // テスト進行中の画面遷移警告フック（条件分岐前に配置）
  const answeredCount = Object.keys(answers).length
  const hasAnswers = answeredCount > 0
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { confirmNavigation } = useTestProgress({
    isTestActive: testStarted && !testResult,
    hasAnswers,
    testTitle: test?.title || 'テスト',
  })

  // テストデータ読み込み
  useEffect(() => {
    const loadTest = async () => {
      if (!testId) {
        setError(t('take.testIdMissing'))
        setLoading(false)
        return
      }

      if (!enrollmentId) {
        setError(t('take.enrollmentIdMissing'))
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const testData = await TestService.getTest(testId)
        setTest(testData)
      } catch (err: any) {
        console.error('Test loading failed:', err)
        setError(t('take.loadError'))
      } finally {
        setLoading(false)
      }
    }

    loadTest()
  }, [testId, enrollmentId, t])

  // テスト開始
  const handleStartTest = () => {
    setTestStarted(true)
    setTestStartTime(new Date())
  }

  // 回答変更
  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }))
  }

  // 前の問題
  const handlePrevQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))
  }

  // 次の問題
  const handleNextQuestion = () => {
    if (test && currentQuestionIndex < test.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1)
    }
  }

  // 提出確認
  const handleSubmitConfirm = () => {
    setConfirmSubmitOpen(true)
  }

  // テスト提出
  const handleSubmitTest = async () => {
    if (!test || !user || !testStartTime || !enrollmentId) return

    try {
      setSubmitting(true)
      setConfirmSubmitOpen(false)

      const submission = {
        testId: test.id,
        userId: user.id,
        enrollmentId,
        answers,
        startedAt: testStartTime.toISOString(),
      }

      const response = await TestService.submitTest(submission)
      setTestResult(response.result)
      setResultModalOpen(true)
    } catch (err: any) {
      console.error('Test submission failed:', err)
      setError(t('take.submitError'))
    } finally {
      setSubmitting(false)
    }
  }

  // 時間切れ
  const handleTimeUp = () => {
    handleSubmitTest()
  }

  // 再挑戦
  const handleRetake = () => {
    setTestStarted(false)
    setTestStartTime(null)
    setCurrentQuestionIndex(0)
    setAnswers({})
    setTestResult(null)
  }

  // 戻る
  const handleBack = () => {
    navigate('/my-enrollments')
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error || !test) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('take.testNotFound')}
        </Alert>
        <Button onClick={handleBack}>{t('take.back')}</Button>
      </Box>
    )
  }

  // テスト開始前画面
  if (!testStarted) {
    return (
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          overflowX: 'hidden',
        }}
      >
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mx: { xs: 0, sm: 1 } }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
          >
            {test.title}
          </Typography>

          <Typography variant="body1" paragraph>
            {test.description}
          </Typography>

          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {t('take.testDetails')}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>{t('take.categoryLabel')}:</strong> {test.complianceRule.category}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>{t('take.questionCountLabel')}:</strong> {test.questions.length}
              {t('take.questionsUnit')}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>{t('take.passingScoreLabel')}:</strong> {test.passingScore}
              {t('take.pointsUnit')}
            </Typography>
            {test.timeLimit && (
              <Typography variant="body2" paragraph>
                <strong>{t('take.timeLimitLabel')}:</strong> {test.timeLimit}
                {t('take.minutesUnit')}
              </Typography>
            )}
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {t('take.importantNotes')}
            </Typography>
            <Typography variant="body2">
              • {t('take.noteNoSave')}
              <br />• {t('take.noteNavigate')}
              <br />• {t('take.noteBack')}
              <br />• {t('take.noteAnswerAll')}
              <br />
              {test.timeLimit && `• ${t('take.noteTimeLimit')}`}
            </Typography>
          </Alert>

          <Box display="flex" gap={2}>
            <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />}>
              {t('take.back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleStartTest}
              startIcon={<PlayArrowIcon />}
              size="large"
            >
              {t('take.start')}
            </Button>
          </Box>
        </Paper>
      </Box>
    )
  }

  const currentQuestion = test.questions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === test.questions.length - 1

  // confirmNavigationは直接使用していないが、フック内で自動的に画面遷移イベントリスナーが登録される

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        overflowX: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: test.timeLimit ? { xs: '1fr', md: '250px 1fr' } : '1fr',
          gap: { xs: 2, sm: 3 },
        }}
      >
        {/* タイマー（制限時間がある場合） - デスクトップ表示 */}
        {test.timeLimit && testStartTime && (
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TestTimer
              timeLimit={test.timeLimit}
              startTime={testStartTime}
              onTimeUp={handleTimeUp}
            />
          </Box>
        )}

        {/* メインコンテンツ */}
        <Box
          sx={{
            minWidth: 0,
            width: '100%',
            mt: { xs: test.timeLimit ? 6 : 0, md: 0 }, // モバイルでフロートタイマーのマージン
          }}
        >
          {/* プログレス */}
          <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={2}>
              <Typography
                variant="h6"
                sx={{
                  flexGrow: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                {test.title}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  fontWeight: 'medium',
                }}
              >
                {currentQuestionIndex + 1} / {test.questions.length}
              </Typography>
            </Box>

            <Stepper activeStep={currentQuestionIndex} alternativeLabel>
              {test.questions.map((_, index) => (
                <Step key={index}>
                  <StepLabel />
                </Step>
              ))}
            </Stepper>
          </Paper>

          {/* 現在の問題 */}
          <QuestionCard
            question={currentQuestion}
            questionNumber={currentQuestionIndex + 1}
            answer={answers[currentQuestion.id] || ''}
            onAnswerChange={handleAnswerChange}
            disabled={submitting}
          />

          {/* ナビゲーション */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            mt={3}
            sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
          >
            <Button
              variant="outlined"
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0 || submitting}
              startIcon={<ArrowBackIcon />}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: { xs: 'auto', sm: '120px' },
              }}
            >
              {t('take.prevQuestion')}
            </Button>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                whiteSpace: 'nowrap',
              }}
            >
              {t('take.answered')}: {answeredCount} / {test.questions.length}
            </Typography>

            {isLastQuestion ? (
              <Button
                variant="contained"
                onClick={handleSubmitConfirm}
                disabled={submitting}
                startIcon={<SendIcon />}
                color="primary"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: { xs: 'auto', sm: '120px' },
                }}
              >
                {t('take.submit')}
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={handleNextQuestion}
                disabled={submitting}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: { xs: 'auto', sm: '120px' },
                }}
              >
                {t('take.nextQuestion')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* フロートタイマー（モバイル表示） */}
      {test.timeLimit && testStartTime && (
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <FloatingTimer
            timeLimit={test.timeLimit}
            startTime={testStartTime}
            onTimeUp={handleTimeUp}
          />
        </Box>
      )}

      {/* 提出確認ダイアログ */}
      <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)}>
        <DialogTitle>{t('take.confirmSubmitTitle')}</DialogTitle>
        <DialogContent>
          <Typography paragraph>{t('take.confirmSubmit')}</Typography>
          <Typography variant="body2" color="textSecondary">
            {t('take.answeredCount', { answered: answeredCount, total: test.questions.length })}
          </Typography>
          {answeredCount < test.questions.length && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('take.unansweredWarning')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmitOpen(false)}>{t('take.cancel')}</Button>
          <Button onClick={handleSubmitTest} variant="contained" disabled={submitting}>
            {submitting ? t('take.submitting') : t('take.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 結果表示 */}
      <TestResultModal
        open={resultModalOpen}
        onClose={() => {
          setResultModalOpen(false)
          navigate('/my-enrollments')
        }}
        result={testResult}
        testId={testId || ''}
        test={test}
        onRetake={handleRetake}
      />
    </Box>
  )
}
